<script setup lang="ts">
/**
 *
 * @name AlertaExpiracionFirmantes
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE QUE MUESTRA UNA ALERTA DE EXPIRACION DE FIRMANTES
 *
 * responsabilidades:
 * R1: PERMITE VISUALIZAR LA ALERTA DE EXPIRACION DE  CERTIFICADOS DE FIRMANTES
 * R2: PERMITE CERRAR LA ALERTA DE EXPIRACION DE FIRMANTES
 *
 * @changelog 1.0.0 - 2026-03-12 - @Jorel254 - Versión inicial del componente
 */

import { ref, onMounted } from 'vue'
import { isExpired, isNearExpiry } from '@/components/utils/functions'
import { formatDate } from '@/utils/dateFormatter'
const props = defineProps<{
	selectedInstitutionResponsables: Record<string, any>
}>()

const showModal = ref(false)
const responsables = ref<Record<string, any>>()
const hasExpiredCertificates = ref(false)
const hasNearExpiryCertificates = ref(false)
const responsablesArray = ref<any[]>([])
const closeModal = () => {
	showModal.value = false
}

onMounted(() => {
	responsables.value = props.selectedInstitutionResponsables
	responsablesArray.value = Object.values(responsables.value)
	responsablesArray.value = responsablesArray.value.filter(responsable => isExpired(responsable.cer?.x509?.notAfter) || isNearExpiry(responsable.cer?.x509?.notAfter))
	validateCertificates()
	if (hasExpiredCertificates.value || hasNearExpiryCertificates.value) {
		showModal.value = true
	}
})
const validateCertificates = () => {
	let expiredCertificates = 0
	let nearExpiryCertificates = 0
	for (const responsable of responsablesArray.value) {
		if (isExpired(responsable.cer?.x509?.notAfter)) {
			expiredCertificates++
		}
		if (isNearExpiry(responsable.cer?.x509?.notAfter)) {
			nearExpiryCertificates++
		}
	}
	if (expiredCertificates > 0) {
		hasExpiredCertificates.value = true
	}
	if (nearExpiryCertificates > 0) {
		hasNearExpiryCertificates.value = true
	}
}
</script>

<template>
	<div>
		<VaModal v-model="showModal" blur hide-default-actions no-esc-dismiss no-outside-dismiss no-dismiss max-height="90vh" max-width="1200px">
			<VaCard class="mantenimiento-card">
				<div class="mantenimiento-header" :class="hasExpiredCertificates ? 'bg-gradient-error' : 'bg-gradient-warning'">
					<div class="mantenimiento-header-left">
						<div class="status-badge" :class="hasExpiredCertificates ? 'status-error' : 'status-warning'">
							<VaIcon v-if="hasExpiredCertificates" class="status-icon" name="error" />
							<VaIcon v-else-if="hasNearExpiryCertificates" class="status-icon" name="warning" />
							<span v-if="hasExpiredCertificates" style="font-size: small">Se han encontrado certificados de responsables de firma que han expirado.</span>
							<span v-else-if="hasNearExpiryCertificates" style="font-size: small">Se han encontrado certificados de responsables de firma que están próximos a expirar.</span>
						</div>
					</div>
				</div>

				<VaCardContent class="mantenimiento-body">
					<div v-for="responsable in responsablesArray" :key="responsable.curp">
						<div class="mantenimiento-range" :class="isExpired(responsable.cer?.x509?.notAfter) ? 'status-error' : isNearExpiry(responsable.cer?.x509?.notAfter) ? 'status-warning' : ' '">
							<div class="mantenimiento-range-header">
								<div class="mantenimiento-range-item mantenimiento-range-item--primary">
									<span class="range-label">Nombre:</span>
									<span class="range-value">{{ responsable.nombre }}</span>
									<span class="range-value">{{ responsable.primerApellido }}</span>
									<span class="range-value">{{ responsable.segundoApellido }}</span>
								</div>
								<div class="mantenimiento-range-status-pill" v-if="isExpired(responsable.cer?.x509?.notAfter)">
									<VaIcon name="error" class="status-pill-icon" />
									<span>Certificado expirado</span>
								</div>
								<div class="mantenimiento-range-status-pill mantenimiento-range-status-pill--warning" v-else-if="isNearExpiry(responsable.cer?.x509?.notAfter)">
									<VaIcon name="warning" class="status-pill-icon" />
									<span>Próximo a expirar</span>
								</div>
							</div>
							<div class="mantenimiento-range-item">
								<VaIcon name="event" class="range-icon" />
								<span class="range-label">Fecha de expiración:</span>
								<span class="range-value">{{ formatDate(responsable.cer?.x509?.notAfter) }}</span>
							</div>
						</div>
					</div>
					<VaDivider />
					<section class="efirma-illustration" aria-labelledby="efirma-atencion-title">
						<div class="efirma-callout" :class="hasExpiredCertificates ? 'efirma-callout--critical' : 'efirma-callout--warning'">
							<div class="efirma-callout__icon-wrap">
								<VaIcon name="priority_high" class="efirma-callout__icon" />
							</div>
							<div class="efirma-callout__body">
								<h3 id="efirma-atencion-title" class="efirma-callout__title">Atención: impacto en su operación</h3>
								<p class="efirma-callout__lead">
									Si no renuevan a tiempo las <strong>E-Firma</strong> de los responsables en caso de que hayan expirado o estén próximos a expirar, el sistema <strong>no podrá firmar</strong> títulos
									ni certificados electrónicos.
								</p>
								<ul class="efirma-callout__list">
									<li>
										<VaIcon name="block" class="efirma-callout__list-icon" />
										<span>Sin certificado vigente → <strong>no hay firma digital</strong></span>
									</li>
									<li>
										<VaIcon name="description" class="efirma-callout__list-icon" />
										<span>Sin firma → <strong>no se emiten</strong> títulos ni certificados</span>
									</li>
									<li>
										<VaIcon name="pause_circle" class="efirma-callout__list-icon" />
										<span>La operación queda <strong>detenida</strong> hasta renovar</span>
									</li>
								</ul>
							</div>
						</div>

						<div class="efirma-diagram-card">
							<header class="efirma-diagram-card__header">
								<VaIcon name="account_tree" class="efirma-diagram-card__header-icon" />
								<div>
									<p class="efirma-diagram-card__kicker">Guía visual</p>
									<p class="efirma-diagram-card__title">Diagrama de renovación de E-Firma (SAT)</p>
									<p class="efirma-diagram-card__hint">Siga los pasos del diagrama para tramitar la renovación antes del vencimiento.</p>
								</div>
							</header>
							<div class="efirma-diagram-card__frame">
								<img src="/assets/logo/digramaEfirma.png" alt="Diagrama paso a paso para renovar la E-Firma en el portal del SAT" class="efirma-diagram-card__img" loading="lazy" />
							</div>
						</div>
					</section>
				</VaCardContent>
				<div class="footer-actions">
					<div class="flex justify-end">
						<VaButton class="action-button" color="primary" @click="closeModal">
							<VaIcon name="close" class="mr-2" />
							Cerrar
						</VaButton>
					</div>
				</div>
			</VaCard>
		</VaModal>
	</div>
</template>

<style scoped>
.mantenimiento-card {
	border-radius: 12px;
	overflow: hidden;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
}

.mantenimiento-header {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
}
.mantenimiento-header-left {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.mantenimiento-body {
	padding: 1.25rem;
	flex: 1 1 auto;
	overflow-y: auto;
}

.mantenimiento-range {
	margin-top: 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	padding: 0.75rem 1rem;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	background: #f8f9fa;
	box-shadow: 0 8px 16px rgba(15, 23, 42, 0.05);
}

.mantenimiento-range-item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #1a2a3a;
	font-size: 0.9rem;
}
.range-icon {
	color: #007bff;
}
.range-label {
	font-weight: 600;
	color: #6c757d;
}
.range-value {
	font-weight: 600;
}

.status-badge {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
	border-radius: 12px;
	font-size: 0.875rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	width: fit-content;
}
.status-warning {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
	border: 1px solid #fcd34d;
}
.status-error {
	background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
	color: #991b1b;
	border: 1px solid #f87171;
}

.status-icon {
	font-size: 1rem;
}

.mantenimiento-range-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 0.75rem;
}

.mantenimiento-range-item--primary {
	flex-wrap: wrap;
}

.mantenimiento-range-status-pill {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.25rem 0.7rem;
	border-radius: 999px;
	background: rgba(220, 38, 38, 0.1);
	color: #b91c1c;
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	white-space: nowrap;
}

.mantenimiento-range-status-pill--warning {
	background: rgba(245, 158, 11, 0.1);
	color: #b45309;
}

.status-pill-icon {
	font-size: 0.9rem;
}

.bg-gradient-warning {
	background: linear-gradient(90deg, #f59e0b 0%, #b45309 100%);
}
.bg-gradient-error {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}

/* Bloque ilustrativo: impacto + flujo + diagrama E-Firma */
.efirma-illustration {
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	margin-bottom: 0.5rem;
}

.efirma-callout {
	display: flex;
	gap: 1rem;
	padding: 1rem 1.15rem;
	border-radius: 14px;
	border: 1px solid;
	align-items: flex-start;
}

.efirma-callout--warning {
	background: linear-gradient(145deg, #fffbeb 0%, #fef3c7 100%);
	border-color: #f59e0b;
	color: #78350f;
}

.efirma-callout--critical {
	background: linear-gradient(145deg, #fef2f2 0%, #fee2e2 100%);
	border-color: #ef4444;
	color: #7f1d1d;
}

.efirma-callout__icon-wrap {
	flex-shrink: 0;
	width: 2.75rem;
	height: 2.75rem;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.06);
}

.efirma-callout--critical .efirma-callout__icon-wrap {
	background: rgba(185, 28, 28, 0.12);
}

.efirma-callout__icon {
	font-size: 1.5rem;
	color: #b45309;
}

.efirma-callout--critical .efirma-callout__icon {
	color: #b91c1c;
}

.efirma-callout__title {
	margin: 0 0 0.5rem;
	font-size: 1.05rem;
	font-weight: 800;
	line-height: 1.3;
	letter-spacing: -0.02em;
}

.efirma-callout__lead {
	margin: 0 0 0.75rem;
	font-size: 0.9rem;
	line-height: 1.55;
	opacity: 0.95;
}

.efirma-callout__list {
	margin: 0;
	padding-left: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.efirma-callout__list li {
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	font-size: 0.85rem;
	line-height: 1.45;
}

.efirma-callout__list-icon {
	flex-shrink: 0;
	margin-top: 2px;
	font-size: 1.1rem;
	opacity: 0.85;
}

.efirma-diagram-card {
	border-radius: 14px;
	border: 1px solid #e2e8f0;
	background: linear-gradient(180deg, #fafafa 0%, #f4f4f5 100%);
	overflow: hidden;
	box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
}

.efirma-diagram-card__header {
	display: flex;
	gap: 0.85rem;
	padding: 0.9rem 1rem;
	background: #fff;
	border-bottom: 1px solid #e2e8f0;
	align-items: flex-start;
}

.efirma-diagram-card__header-icon {
	font-size: 1.75rem;
	color: #0ea5e9;
	flex-shrink: 0;
	margin-top: 2px;
}

.efirma-diagram-card__kicker {
	margin: 0;
	font-size: 0.65rem;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: #64748b;
}

.efirma-diagram-card__title {
	margin: 0.15rem 0 0.35rem;
	font-size: 0.95rem;
	font-weight: 800;
	color: #0f172a;
	line-height: 1.3;
}

.efirma-diagram-card__hint {
	margin: 0;
	font-size: 0.8rem;
	color: #64748b;
	line-height: 1.45;
}

.efirma-diagram-card__frame {
	padding: 0.75rem;
	background: #fff;
	/* Sin max-height ni overflow: el diagrama se muestra completo; el scroll queda solo en el modal si hace falta */
	overflow: visible;
}

.efirma-diagram-card__img {
	display: block;
	width: 100%;
	height: auto;
	max-width: 100%;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
}

.footer-actions {
	padding: 0.75rem 1.25rem 1rem;
	border-top: 1px solid #e5e7eb;
	background: #ffffff;
	flex-shrink: 0;
}

@media (max-width: 768px) {
	.mantenimiento-header {
		padding: 0.9rem 1rem;
	}
	.mantenimiento-body {
		padding: 0.75rem 1rem;
	}
}
</style>
